import React from 'react';
import { getCategoriesListForFin, saveCategoryFin } from "../../util/APIUtils";
import swal from 'sweetalert';
import {
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Card,
    CardBody,
    CardHeader,
    Form,
    FormGroup,
    Label,
    Input,
    Row,
    Col,
    Button,
    Alert
} from "reactstrap";

const AssetsLiabilitiesForm = ({
    onSubmit,
    onCancel,
    onFileChange,
    initialData,
    isSubmitting = false,
    error = null
}) => {
    // Ensure initialData is always an object
    const safeInitialData = initialData && typeof initialData === 'object' ? initialData : {};
    const [categories, setCategories] = React.useState([]);
    const [isLoading, setIsLoading] = React.useState(false);
    const [showNewCategoryModal, setShowNewCategoryModal] = React.useState(false);
    const [newCategory, setNewCategory] = React.useState({ category: '', type: 'ASSET' });
    const [isSavingCategory, setIsSavingCategory] = React.useState(false);
    const [formData, setFormData] = React.useState({
        recordType: safeInitialData.recordType || 'ASSET',
        categoryId: safeInitialData.categoryId || '',
        name: safeInitialData.name || '',
        description: safeInitialData.description || '',
        amount: safeInitialData.amount || '',
        location: safeInitialData.location || '',
        owned: safeInitialData.owned === undefined ? true : safeInitialData.owned,
        lender: safeInitialData.lender || '',
        dueDate: safeInitialData.dueDate || '',
        recordStatus: safeInitialData.recordStatus === undefined ? true : safeInitialData.recordStatus,
        documents: safeInitialData.documents || []
    });

    React.useEffect(() => {
        fetchCategories();
    }, [formData.recordType]); // Refetch when record type changes

    const fetchCategories = async () => {
        try {
            setIsLoading(true);
            const data = await getCategoriesListForFin();

            // Filter categories based on record type
            const filteredCategories = data.filter(cat => {
                if (formData.recordType === 'ASSET') {
                    return cat.type === 'ASSET';
                } else if (formData.recordType === 'LIABILITY') {
                    return cat.type === 'LIABILITY';
                } else {
                    return true;
                }
            });

            setCategories(filteredCategories);
        } catch (error) {
            console.error('Error fetching categories:', error);
            swal("Error", "Failed to fetch categories", "error");
        } finally {
            setIsLoading(false);
        }
    };

    // Reset category when record type changes
    React.useEffect(() => {
        setFormData(prev => ({
            ...prev,
            categoryId: ''
        }));
    }, [formData.recordType]);

    const handleChange = (e) => {
        const { name, value, type, checked, files } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked :
                type === 'file' ? Array.from(files) : value
        }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        onSubmit(formData);
    };

    const handleNewCategoryChange = (e) => {
        const { name, value } = e.target;
        setNewCategory(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleNewCategorySubmit = async (e) => {
        e.preventDefault();
        try {
            setIsSavingCategory(true);
            await saveCategoryFin(newCategory);
            await fetchCategories(); // Refresh the list
            setShowNewCategoryModal(false);
            setNewCategory({ category: '', type: formData.recordType }); // Reset form with current record type
            swal("Success", "Category added successfully", "success");
        } catch (error) {
            console.error('Error saving category:', error);
            swal("Error", "Failed to save category", "error");
        } finally {
            setIsSavingCategory(false);
        }
    };

    return (
        <Card className="shadow-sm" style={{ position: 'relative' }}>
            {isSubmitting && (
                <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    background: 'rgba(255,255,255,0.7)',
                    zIndex: 10,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}>
                    <span className="spinner-border text-primary" style={{ width: '3rem', height: '3rem' }} role="status" />
                </div>
            )}
            <CardHeader style={{
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                padding: "15px 20px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                borderBottom: "none"
            }}>
                <div>
                    <h5 className="m-0 font-weight-bold" style={{ color: "#fff" }}>
                        <i className="fa fa-plus-circle" style={{ marginRight: "10px" }}></i>
                        {safeInitialData && safeInitialData.recordId ? 'Edit Record' : 'Add New Record'}
                    </h5>
                    <small style={{ color: "rgba(255,255,255,0.8)" }}>Enter the details below</small>
                </div>
                <div>
                    <Button
                        className="px-4"
                        onClick={onCancel}
                        style={{
                            background: "rgba(255,255,255,0.2)",
                            border: "1px solid rgba(255,255,255,0.3)",
                            color: "#fff",
                            borderRadius: "8px",
                            transition: "all 0.3s ease"
                        }}
                        disabled={isSubmitting}
                    >
                        <i className="fa fa-arrow-left mr-2"></i>
                        Back to List
                    </Button>
                </div>
            </CardHeader>
            <CardBody>
                {error && (
                    <Alert color="danger" className="mb-4">
                        {error}
                    </Alert>
                )}

                <Form onSubmit={handleSubmit}>
                    <Row>
                        <Col md={6}>
                            <FormGroup>
                                <Label for="recordType" className="font-weight-bold small mb-1" style={{ fontSize: '0.85rem' }}>Type</Label>
                                <Input
                                    type="select"
                                    name="recordType"
                                    id="recordType"
                                    value={formData.recordType}
                                    onChange={handleChange}
                                    className="form-control form-control-sm"
                                    style={{ fontSize: '0.95rem' }}
                                    disabled={isSubmitting}
                                >
                                    <option value="ASSET">Asset</option>
                                    <option value="LIABILITY">Liability</option>
                                </Input>
                            </FormGroup>
                        </Col>
                        <Col md={6}>
                            <FormGroup>
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <Label for="categoryId" className="font-weight-bold small mb-1" style={{ fontSize: '0.85rem' }}>
                                        Category {isLoading && <small className="text-muted">(Loading...)</small>}
                                    </Label>
                                    <Button
                                        type="button"
                                        color="light"
                                        size="sm"
                                        onClick={() => {
                                            setNewCategory({ category: '', type: formData.recordType });
                                            setShowNewCategoryModal(true);
                                        }}
                                        className="text-primary"
                                    >
                                        <i className="fa fa-plus-circle mr-1"></i>
                                        Add Category
                                    </Button>
                                </div>
                                <Input
                                    type="select"
                                    name="categoryId"
                                    id="categoryId"
                                    value={formData.categoryId}
                                    onChange={handleChange}
                                    className="form-control form-control-sm"
                                    style={{ fontSize: '0.95rem' }}
                                    required
                                    disabled={isLoading || isSubmitting}
                                >
                                    <option value="">Select Category</option>
                                    {categories.map(cat => (
                                        <option key={cat.id} value={cat.id}>
                                            {cat.category} ({cat.type})
                                        </option>
                                    ))}
                                </Input>
                                {isLoading && (
                                    <div className="text-center mt-1">
                                        <span className="spinner-border spinner-border-sm text-primary" role="status" />
                                    </div>
                                )}

                                {/* New Category Modal */}
                                <Modal isOpen={showNewCategoryModal} toggle={() => setShowNewCategoryModal(false)}>
                                    <ModalHeader toggle={() => setShowNewCategoryModal(false)}>
                                        Add Category
                                    </ModalHeader>
                                    <ModalBody>
                                        <Form onSubmit={handleNewCategorySubmit}>
                                            <FormGroup>
                                                <Label for="newCategoryName" className="font-weight-bold">Category Name</Label>
                                                <Input
                                                    type="text"
                                                    id="newCategoryName"
                                                    name="category"
                                                    value={newCategory.category}
                                                    onChange={handleNewCategoryChange}
                                                    placeholder="Enter category name"
                                                    required
                                                    className="form-control-lg"
                                                />
                                            </FormGroup>
                                            <FormGroup>
                                                <Label for="newCategoryType" className="font-weight-bold">Type</Label>
                                                <Input
                                                    type="select"
                                                    id="newCategoryType"
                                                    name="type"
                                                    value={newCategory.type}
                                                    onChange={handleNewCategoryChange}
                                                    required
                                                    className="form-control-lg"
                                                >
                                                    <option value="ASSET">Asset</option>
                                                    <option value="LIABILITY">Liability</option>
                                                </Input>
                                            </FormGroup>
                                            <div className="text-right mt-4">
                                                <Button
                                                    type="button"
                                                    color="secondary"
                                                    onClick={() => setShowNewCategoryModal(false)}
                                                    className="mr-2 btn-hover-fade"
                                                    disabled={isSavingCategory}
                                                    style={{
                                                        background: '#6c757d',
                                                        border: 'none',
                                                        transition: 'all 0.3s ease',
                                                        opacity: 1
                                                    }}
                                                >
                                                    Cancel
                                                </Button>
                                                <Button
                                                    type="submit"
                                                    color="primary"
                                                    disabled={isSavingCategory}
                                                    style={{
                                                        background: '#007bff',
                                                        border: 'none',
                                                        transition: 'all 0.3s ease',
                                                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                                    }}
                                                >
                                                    {isSavingCategory ? (
                                                        <div className="d-inline-block">
                                                            <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                                                            Saving...
                                                        </div>
                                                    ) : 'Save Category'}
                                                </Button>
                                            </div>
                                        </Form>
                                    </ModalBody>
                                </Modal>
                            </FormGroup>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={6}>
                            <FormGroup>
                                <Label for="name" className="font-weight-bold small mb-1" style={{ fontSize: '0.85rem' }}>Name</Label>
                                <Input
                                    type="text"
                                    name="name"
                                    id="name"
                                    placeholder="Enter name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    className="form-control form-control-sm"
                                    style={{ fontSize: '0.95rem' }}
                                    required
                                    disabled={isSubmitting}
                                />
                            </FormGroup>
                        </Col>
                        <Col md={6}>
                            <FormGroup>
                                <Label for="description" className="font-weight-bold small mb-1" style={{ fontSize: '0.85rem' }}>Description</Label>
                                <Input
                                    type="textarea"
                                    name="description"
                                    id="description"
                                    placeholder="Enter description"
                                    value={formData.description}
                                    onChange={handleChange}
                                    rows={2}
                                    className="form-control form-control-sm"
                                    style={{ fontSize: '0.95rem' }}
                                    disabled={isSubmitting}
                                />
                            </FormGroup>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={6}>
                            <FormGroup>
                                <Label for="amount" className="font-weight-bold small mb-1" style={{ fontSize: '0.85rem' }}>Amount</Label>
                                <Input
                                    type="number"
                                    name="amount"
                                    id="amount"
                                    placeholder="Enter amount"
                                    value={formData.amount}
                                    onChange={handleChange}
                                    className="form-control form-control-sm"
                                    style={{ fontSize: '0.95rem' }}
                                    required
                                    disabled={isSubmitting}
                                />
                            </FormGroup>
                        </Col>
                        <Col md={6}>
                            <FormGroup>
                                <Label for="location" className="font-weight-bold small mb-1" style={{ fontSize: '0.85rem' }}>Location</Label>
                                <Input
                                    type="text"
                                    name="location"
                                    id="location"
                                    placeholder="Enter location"
                                    value={formData.location}
                                    onChange={handleChange}
                                    className="form-control form-control-sm"
                                    style={{ fontSize: '0.95rem' }}
                                    disabled={isSubmitting}
                                />
                            </FormGroup>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={6}>
                            <FormGroup>
                                <Label for="documents" className="font-weight-bold small mb-1" style={{ fontSize: '0.85rem' }}>Documents</Label>
                                <Input
                                    type="file"
                                    name="documents"
                                    id="documents"
                                    onChange={e => onFileChange(e.target.files[0])}
                                    className="form-control form-control-sm"
                                    style={{ fontSize: '0.95rem' }}
                                    disabled={isSubmitting}
                                />
                                <small className="text-muted">
                                    Upload supporting documents (optional)
                                </small>
                            </FormGroup>
                        </Col>
                        <Col md={6}>
                            <FormGroup className="mt-4">
                                <div className="custom-control custom-checkbox">
                                    <Input
                                        type="checkbox"
                                        className="custom-control-input"
                                        name="owned"
                                        id="owned"
                                        checked={formData.owned}
                                        onChange={handleChange}
                                        disabled={isSubmitting}
                                    />
                                    <Label className="custom-control-label font-weight-bold small" for="owned" style={{ fontSize: '0.85rem' }}>
                                        Owned
                                    </Label>
                                </div>
                            </FormGroup>
                        </Col>
                    </Row>

                    {!formData.owned && (
                        <Row>
                            <Col md={6}>
                                <FormGroup>
                                    <Label for="lender" className="font-weight-bold small mb-1" style={{ fontSize: '0.85rem' }}>Lender</Label>
                                    <Input
                                        type="text"
                                        name="lender"
                                        id="lender"
                                        placeholder="Enter lender name"
                                        value={formData.lender}
                                        onChange={handleChange}
                                        className="form-control form-control-sm"
                                        style={{ fontSize: '0.95rem' }}
                                        required={!formData.owned}
                                        disabled={isSubmitting}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={6}>
                                <FormGroup>
                                    <Label for="dueDate" className="font-weight-bold small mb-1" style={{ fontSize: '0.85rem' }}>Due Date</Label>
                                    <Input
                                        type="date"
                                        name="dueDate"
                                        id="dueDate"
                                        value={formData.dueDate}
                                        onChange={handleChange}
                                        className="form-control form-control-sm"
                                        style={{ fontSize: '0.95rem' }}
                                        required={!formData.owned}
                                        disabled={isSubmitting}
                                    />
                                </FormGroup>
                            </Col>
                        </Row>
                    )}
                    <div className="mt-4 d-flex justify-content-end">
                        <Button
                            type="button"
                            color="danger"
                            className="mr-2 px-4 btn-hover-scale"
                            onClick={onCancel}
                            style={{
                                background: '#dc3545',
                                border: 'none',
                                transition: 'all 0.2s ease',
                                transform: 'scale(1)'
                            }}
                            disabled={isSubmitting}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            color="success"
                            className="px-4 btn-hover-pulse"
                            disabled={isSubmitting}
                            style={{
                                background: '#28a745',
                                border: 'none',
                                transition: 'all 0.3s ease',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                            }}
                        >
                            {isSubmitting ? (
                                <div className="d-inline-block">
                                    <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                                    Saving...
                                </div>
                            ) : 'Save Record'}
                        </Button>
                    </div>
                </Form>
            </CardBody>
        </Card>
    );
};

export default AssetsLiabilitiesForm;



// WEBPACK FOOTER //
// ./src/components/CustomComponents/AssetsLiabilitiesForm.js